import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Typography, 
  Button, 
  Space, 
  Form,
  Row,
  Col,
  Input,
  Select,
  Spin,
  Modal, // Modal 추가
  message // message 추가
} from 'antd';
import { 
  ArrowLeftOutlined,
  CheckCircleOutlined
} from '@ant-design/icons';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import AdminLayout from '@/components/Layouts/AdminLayout/AdminLayout';
import { adminRegistrationApi } from '@/services/api';
import { sanitizeSlug } from '@/utils/slug';

const { Title, Text } = Typography;

interface RegistrationDetail {
  id: string;
  academyId: string;
  academyName: string;
  representativeName: string;
  representativePhone: string;
  representativeEmail: string;
  businessNumber: string;
  userCount: number;
  academyUrl: string;
  // 가입상태: 승인대기, 승인, 반려, 재신청
  registrationStatus: 'pending' | 'approved' | 'rejected' | 'reapplied';
  rejectionReason?: string;
  applicationDate: string;
}

const ACADEMY_DOMAIN = (process.env.REACT_APP_ACADEMY_HOST || 'https://alphaca.mvpick.net').replace(/\/+$/, '');

const buildFullAcademyUrl = (value?: string) => {
  const slug = sanitizeSlug(value || '');
  return slug ? `${ACADEMY_DOMAIN}/${slug}` : '';
};

const extractSlugFromUrl = (value?: string) => {
  if (!value) return '';
  const trimmed = value.trim();
  if (!trimmed) return '';
  try {
    const parsed = trimmed.includes('://') ? new URL(trimmed) : new URL(`${ACADEMY_DOMAIN}/${trimmed}`);
    const pathSlug = parsed.pathname.replace(/^\/+/, '').split('/')[0] || '';
    return sanitizeSlug(pathSlug);
  } catch {
    return sanitizeSlug(trimmed);
  }
};

const RegistrationDetail: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const registrationId = (params as any).registrationId;
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [registrationData, setRegistrationData] = useState<RegistrationDetail | null>(null);
  const [form] = Form.useForm();
  const [rejectModalVisible, setRejectModalVisible] = useState(false); // 반려 모달 상태
  const [rejectReason, setRejectReason] = useState(''); // 반려사유
  const [rejectMessageModalVisible, setRejectMessageModalVisible] = useState(false); // 반려메시지 모달 상태
  const [rejectMessage, setRejectMessage] = useState(''); // 반려메시지
  const fromPath = (location.state as { from?: string } | null)?.from;

  // 진입 경로에 따른 목록으로 이동 함수
  const handleBackToList = () => {
    const from = searchParams.get('from');
    if (fromPath) {
      navigate(fromPath);
      return;
    }
    if (from === 'registration-list') {
      navigate('/admin/registration'); // 가입신청 관리 리스트로
    } else {
      navigate('/admin/registration'); // 기본적으로 가입신청 관리로
    }
  };

  const handleSave = () => {
    form.validateFields().then((values) => {
      console.log('저장할 데이터:', values);
    });
  };

  const handleUrlCheck = () => {
    const url = form.getFieldValue('academyUrl');
    const slug = sanitizeSlug(url || '');
    if (!slug) {
      message.warning('학원 주소 뒤에 붙을 영문/숫자 값을 입력해주세요.');
      return;
    }
    const fullUrl = buildFullAcademyUrl(slug);

    (async () => {
      try {
        const res: any = await adminRegistrationApi.checkAcademyUrl(fullUrl);
        const ok = res?.data?.available;
        const msg = res?.data?.message || (ok ? '사용 가능한 URL입니다.' : '이미 사용중인 URL입니다.');
        ok ? message.success(msg) : message.error(msg);
      } catch (e) {
        message.error('URL 중복확인 중 오류가 발생했습니다.');
      }
    })();
  };

  const handleReject = () => {
    setRejectModalVisible(true); // 반려 모달 표시
  };

  const handleRejectConfirm = () => {
    (async () => {
      try {
        if (!registrationId) return;
        if (!rejectReason || !rejectReason.trim()) {
          message.error('반려 사유를 입력해주세요.');
          return;
        }
        await adminRegistrationApi.reject(registrationId, rejectReason.trim());
        message.success('반려 처리되었습니다.');
        setRejectModalVisible(false);
        setRejectReason('');
        // 상세 재조회
        setLoading(true);
        await load();
      } catch (e) {
        message.error('반려 처리 중 오류가 발생했습니다.');
      }
    })();
  };

  const handleRejectCancel = () => {
    setRejectModalVisible(false);
    setRejectReason('');
  };

  const handleApprove = () => {
    const slugInput = form.getFieldValue('academyUrl');
    const slug = sanitizeSlug(slugInput || '');
    if (!slug) {
      message.error('승인 시에는 학원 주소 뒤에 붙을 값을 입력해주세요.');
      return;
    }
    const fullUrl = buildFullAcademyUrl(slug);
    const payload = {
      academyName: form.getFieldValue('academyName'),
      representativeName: form.getFieldValue('representativeName'),
      representativePhone: form.getFieldValue('representativePhone'),
      representativeEmail: form.getFieldValue('representativeEmail'),
      businessNumber: form.getFieldValue('businessNumber'),
      userCount: form.getFieldValue('userCount'),
      academyUrl: fullUrl,
    };
    (async () => {
      try {
        await adminRegistrationApi.approve(registrationId, payload);
        message.success('승인 처리되었습니다.');
        setLoading(true);
        await load();
      } catch (error) {
        const errMsg = (error as any)?.response?.data?.message;
        message.error(errMsg || '승인 처리 중 오류가 발생했습니다.');
      }
    })();
  };

  const handleRejectMessageView = () => {
    const msg = registrationData?.rejectionReason || '반려 사유가 등록되어 있지 않습니다.';
    setRejectMessage(msg);
    setRejectMessageModalVisible(true);
  };

  // 서버에서 상세정보 로드
  const load = async () => {
    try {
      const res: any = await adminRegistrationApi.detail(registrationId);
      const d = res?.data;
      if (!d) {
        setRegistrationData(null);
        setLoading(false);
        return;
      }
      const mapped: RegistrationDetail = {
        id: d.id,
        academyId: d.academyId || '',
        academyName: d.academyName,
        representativeName: d.representativeName,
        representativePhone: d.representativePhone || '',
        representativeEmail: d.representativeEmail || '',
        businessNumber: d.businessNumber || '',
        userCount: d.userCount || 0,
        academyUrl: extractSlugFromUrl(d.academyUrl || ''),
        registrationStatus: (d.registrationStatus as any) || 'pending',
        rejectionReason: d.rejectionReason || '',
        applicationDate: (d.applicationDate || '').split('T')[0] || ''
      };
      setRegistrationData(mapped);
      setLoading(false);
    } catch (e) {
      setRegistrationData(null);
      setLoading(false);
    }
  };

  // Form 초기값을 한글로 변환
  const getFormInitialValues = (data: RegistrationDetail) => {
    return {
      ...data,
      registrationStatus: getStatusText(data.registrationStatus)
    };
  };

  // 가입상태 한글 변환 함수
  const getStatusText = (status: string) => {
    const statusMap: Record<string, string> = {
      'pending': '승인대기',
      'approved': '승인',
      'rejected': '반려',
      'reapplied': '재신청'
    };
    return statusMap[status] || status;
  };

  useEffect(() => {
    setLoading(true);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registrationId]);

  // 데이터 로드 후 폼에 값 주입 (initialValues는 mount시에만 반영되므로 보강)
  useEffect(() => {
    if (registrationData) {
      form.setFieldsValue(getFormInitialValues(registrationData));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registrationData]);

  const isApproved = registrationData?.registrationStatus === 'approved';

  return (
    <AdminLayout>
      {loading ? (
        <div style={{ padding: '24px', textAlign: 'center', minHeight: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Spin size="large" tip="로딩 중..." />
        </div>
      ) : !registrationData ? (
        <div style={{ padding: '24px', textAlign: 'center', minHeight: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div>가입신청 정보를 찾을 수 없습니다.</div>
        </div>
      ) : (
        <div style={{ padding: '24px' }}>
          {/* 헤더 */}
          <div style={{ marginBottom: 24 }}>
            <Space style={{ marginBottom: 16 }}>
              <Button 
                icon={<ArrowLeftOutlined />} 
                onClick={handleBackToList}
              >
                목록으로
              </Button>
            </Space>
            
            <Title level={2}>가입신청 상세</Title>
            <Text type="secondary">신청ID: {registrationData.id}</Text>
          </div>

          {/* 상세 정보 */}
          <Card>
            <Form
              form={form}
              layout="vertical"
              initialValues={getFormInitialValues(registrationData)}
            >
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item label="ID" name="academyId">
                    <Input disabled size="large" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item 
                    label="학원명" 
                    name="academyName"
                    rules={[
                      { required: true, message: '학원명을 입력해주세요' },
                      { min: 2, max: 100, message: '학원명은 2~100자로 입력해주세요' }
                    ]}
                  >
                    <Input placeholder="학원명을 입력하세요" size="large" disabled={isApproved} maxLength={100} />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item 
                    label="대표자명" 
                    name="representativeName"
                    rules={[
                      { required: true, message: '대표자명을 입력해주세요' },
                    { min: 2, max: 100, message: '대표자명은 2~100자로 입력해주세요' }
                    ]}
                  >
                    <Input placeholder="대표자명을 입력하세요" size="large" disabled={isApproved} maxLength={100} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item 
                    label="대표자 연락처" 
                    name="representativePhone"
                    rules={[
                    { required: true, message: '연락처를 입력해주세요' },
                    { pattern: /^\d{9,30}$/, message: '숫자만 9~30자로 입력해주세요' }
                    ]}
                  normalize={(v) => (v || '').replace(/\D/g, '')}
                  >
                    <Input placeholder="숫자만 입력" size="large" disabled={isApproved} maxLength={30} inputMode="numeric" pattern="[0-9]*" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item 
                    label="대표자 이메일" 
                    name="representativeEmail"
                    rules={[
                      { required: true, message: '이메일을 입력해주세요' },
                      { type: 'email', message: '올바른 이메일 형식으로 입력해주세요' }
                    ]}
                  >
                    <Input placeholder="example@email.com" size="large" disabled={isApproved} maxLength={120} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="사업자 등록번호" name="businessNumber">
                    <Input disabled size="large" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item 
                    label="사용인원" 
                    name="userCount"
                    rules={[
                      { required: true, message: '사용인원을 선택해주세요' }
                    ]}
                  >
                    <Select size="large" placeholder="사용인원을 선택하세요" disabled={isApproved}>
                      <Select.Option value={25}>1 ~ 25명</Select.Option>
                      <Select.Option value={50}>25 ~ 50명</Select.Option>
                      <Select.Option value={75}>50 ~ 75명</Select.Option>
                      <Select.Option value={100}>75 ~ 100명</Select.Option>
                      <Select.Option value={125}>100 ~ 125명</Select.Option>
                      <Select.Option value={150}>125 ~ 150명</Select.Option>
                      <Select.Option value={175}>150 ~ 175명</Select.Option>
                      <Select.Option value={200}>175 ~ 200명</Select.Option>
                      <Select.Option value={200}>200+명</Select.Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="학원URL" required>
                    <div style={{ display: 'flex', gap: '8px', width: '100%' }}>
                      <Form.Item 
                        name="academyUrl"
                        noStyle
                        rules={[
                          { pattern: /^[a-zA-Z0-9-_]+$/, message: '영문, 숫자, 하이픈, 밑줄만 입력해주세요' }
                        ]}
                      >
                        <Input 
                          placeholder="슬러그 (예: alpha)" 
                          size="large" 
                          addonBefore={`${ACADEMY_DOMAIN}/`}
                          style={{ flex: 1 }}
                          maxLength={200}
                          disabled={isApproved}
                        />
                      </Form.Item>
                      <Button 
                        type="primary"
                        size="large" 
                        onClick={handleUrlCheck}
                        style={{ width: '100px' }}
                        disabled={isApproved}
                      >
                        중복확인
                      </Button>
                    </div>
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item label="가입상태">
                    {registrationData?.registrationStatus === 'rejected' ? (
                      // 반려 상태일 때는 Input.Group으로 버튼과 함께 표시
                      <Input.Group compact>
                        <Form.Item name="registrationStatus" noStyle>
                          <Input 
                            disabled 
                            size="large" 
                            style={{ width: 'calc(100% - 120px)' }}
                          />
                        </Form.Item>
                        <Button 
                          size="large" 
                          onClick={handleRejectMessageView}
                          style={{ width: '120px' }}
                        >
                          반려메시지보기
                        </Button>
                      </Input.Group>
                    ) : (
                      // 반려 상태가 아닐 때는 일반 Input만 표시
                      <Form.Item name="registrationStatus" noStyle>
                        <Input disabled size="large" />
                      </Form.Item>
                    )}
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="가입 신청일" name="applicationDate">
                    <Input disabled size="large" />
                  </Form.Item>
                </Col>
              </Row>

              {/* 버튼 - 가입상태에 따라 조건부 렌더링 */}
              <div style={{ textAlign: 'right', marginTop: 32 }}>
                <Space size="middle">
                  {registrationData?.registrationStatus === 'approved' ? null : registrationData?.registrationStatus === 'rejected' ? (
                    // 반려 상태일 때는 승인 버튼만 노출
                    <Button 
                      type="primary" 
                      size="large"
                      style={{ height: 48, fontSize: 16, padding: '0 32px' }}
                      onClick={handleApprove}
                    >
                      승인하기
                    </Button>
                  ) : (
                    // 승인대기 상태일 때는 반려하기, 승인하기 버튼 모두 노출
                    <>
                      <Button 
                        danger 
                        size="large"
                        style={{ height: 48, fontSize: 16, padding: '0 32px' }}
                        onClick={handleReject}
                      >
                        반려하기
                      </Button>
                      <Button 
                        type="primary" 
                        size="large"
                        style={{ height: 48, fontSize: 16, padding: '0 32px' }}
                        onClick={handleApprove}
                      >
                        승인하기
                      </Button>
                    </>
                  )}
                </Space>
              </div>
            </Form>
          </Card>

          {/* 반려사유 모달 */}
          <Modal
            title="반려사유 입력"
            open={rejectModalVisible}
            onCancel={handleRejectCancel}
            footer={[
              <Button key="cancel" onClick={handleRejectCancel}>
                취소
              </Button>,
              <Button 
                key="confirm" 
                type="primary" 
                danger 
                onClick={handleRejectConfirm}
              >
                반려 처리
              </Button>
            ]}
          >
            <div>
              <p style={{ marginBottom: '16px' }}>
                반려사유를 입력해주세요.
              </p>
              <Input.TextArea
                rows={4}
                placeholder="반려사유를 입력하세요..."
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
              />
            </div>
          </Modal>

          {/* 반려메시지 모달 */}
          <Modal
            title="반려메시지"
            open={rejectMessageModalVisible}
            onCancel={() => setRejectMessageModalVisible(false)}
            footer={[
              <Button key="close" onClick={() => setRejectMessageModalVisible(false)}>
                닫기
              </Button>
            ]}
          >
            <div>
              <p style={{ margin: 0 }}>
                {rejectMessage}
              </p>
            </div>
          </Modal>
        </div>
      )}
    </AdminLayout>
  );
};

export default RegistrationDetail; 